/**
 * FloorMarker — floor decal at the base of a scene: a flat disc on the ground
 * with the decal texture.
 */

import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  Vector3,
  Tools,
  type Mesh,
  type Scene,
  type TransformNode,
} from 'babylonjs';
import { textureFromBase64 } from '../utils/BabylonEditorUtils';
import type { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import FloorDecalImage from '../../../assets/floor_decal_blank.png';

export class FloorMarker {
  readonly mesh: Mesh;
  private material: StandardMaterial;

  constructor(parent: TransformNode, scene: Scene, events: EventBus<EditorEvents>) {
    this.mesh = MeshBuilder.CreatePlane('FloorMarker', { width: 0.5, height: 0.5 }, scene);
    this.mesh.parent = parent;
    this.mesh.isPickable = false;
    this.mesh.position = new Vector3(0, 0.01, 0);
    this.mesh.rotation.x = Tools.ToRadians(90); // lay flat on the ground

    this.material = new StandardMaterial('floor_marker_mat', scene);
    this.material.disableLighting = true;
    this.material.backFaceCulling = false;
    this.material.emissiveColor = new Color3(0, 0, 0);
    this.material.diffuseColor = new Color3(0, 0, 0);
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.alpha = 0;
    this.mesh.material = this.material;

    textureFromBase64(FloorDecalImage, scene)
      .then((texture) => {
        texture.hasAlpha = true;
        this.material.diffuseTexture = texture;
        this.material.alpha = 1;
        events.emit('staticAssets:loaded', { type: 'floorMarker' });
      })
      .catch(() => { /* ignore */ });
  }

  cleanup(): void {
    this.material.diffuseTexture?.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}

export default FloorMarker;
